/**
 * App-rail section of the main navigation (the "Apps" divider + every embedded app).
 * Split out of navigationConfig.ts (file-size limit); spread into `navigationConfig`
 * at the same position, so order and behavior are unchanged.
 */
import {
  LayoutDashboard,
  Layers,
  FileText,
  Gift,
  Truck,
  Building2,
  Store,
  Palette,
  SlidersHorizontal,
  PackageCheck,
  Layers3,
  BarChart3,
  ClipboardCheck,
  Contact,
  Star,
  FileSpreadsheet,
  CreditCard,
  PackageOpen,
  Ticket,
  Mail,
  Workflow,
  UserPlus,
  Inbox,
  Home,
  FileImage,
  Smartphone,
  LayoutGrid,
  Globe,
  MessageSquare,
  Monitor,
  Newspaper,
  // Bot,  // TEMPORARILY UNUSED — re-add when the /ai nav entry is un-hidden (see below)
} from "lucide-react";
import type { NavigationConfigItem } from "./navigationConfig";

export const appsNavigationConfig: NavigationConfigItem[] = [
  // ============================================
  // APPS SECTION - Divider + Embedded Apps
  // ============================================
  {
    type: "divider",
    label: "Apps",
  },

  // Dashboard - Direct Link (main dashboard/homepage)
  {
    name: "หน้าหลัก",
    href: "/",
    icon: Home,
    color: "bg-gradient-to-br from-blue-500 to-purple-600",
    requiresProvisioning: false,
  },

  // Business - Direct Link (main business hub)
  {
    name: "ธุรกิจ",
    href: "/business",
    icon: Building2,
    color: "bg-gradient-to-br from-slate-500 to-slate-600",
    requiresProvisioning: true,
  },

  // CRM - Direct Link (opens embedded CRM)
  {
    name: "CRM",
    href: "/crm",
    icon: UserPlus,
    color: "bg-gradient-to-br from-rose-500 to-rose-600",
    requiresProvisioning: true,
    appAccessKey: "crm",
    appRegistryKey: "crm",
  },

  // Twenty CRM - Direct Link (opens embedded Twenty CRM)
  {
    name: "B2B-CRM",
    railLabel: "B2B",
    href: "/twenty-crm",
    icon: Contact,
    color: "bg-gradient-to-br from-amber-500 to-amber-600",
    requiresProvisioning: true,
  },

  // Inbox - Direct Link (opens embedded Chatwoot inbox)
  {
    name: "กล่องข้อความ",
    railLabel: "ข้อความ",
    href: "/inbox",
    icon: Inbox,
    color: "bg-gradient-to-br from-sky-500 to-sky-600",
    requiresProvisioning: true,
    appAccessKey: "inbox",
    appRegistryKey: "chatwoot",
  },

  // Raven (Team Chat) - Direct Link (opens embedded Raven)
  {
    name: "แชททีม",
    href: "/raven",
    icon: MessageSquare,
    color: "bg-gradient-to-br from-amber-500 to-amber-600",
    requiresProvisioning: true,
    appAccessKey: "raven",
    appRegistryKey: "raven",
  },

  // Insights - Direct Link (opens embedded Insights)
  {
    name: "รายงาน",
    href: "/insights",
    icon: BarChart3,
    color: "bg-gradient-to-br from-indigo-500 to-indigo-600",
    requiresProvisioning: true,
    appAccessKey: "insights",
    appRegistryKey: "insights",
  },

  // Storefront — href for app grid + global bar, subMenu for ERP sidebar drill-in
  {
    name: "สร้างเว็บ",
    href: "/storefront/collections",
    icon: Palette,
    color: "bg-gradient-to-br from-pink-500 to-pink-600",
    accentColor: "pink",
    requiresProvisioning: true,
    subMenu: [
      {
        title: "ร้านค้าออนไลน์",
        items: [
          { name: "หน้าเว็บ", href: "/storefront/page-editor/home", icon: FileText },
          { name: "แผนผังร้าน", href: "/storefront/atlas", icon: Workflow },
          { name: "คอลเลกชัน", href: "/storefront/collections", icon: Layers3 },
          { name: "การ์ดสินค้า", href: "/storefront/page-editor/product-card", icon: LayoutGrid },
        ],
      },
      {
        title: "การชำระเงิน",
        items: [
          { name: "วิธีชำระเงิน", href: "/storefront/payment-methods", icon: CreditCard },
          { name: "สลิปการชำระ", href: "/storefront/payment-slips", icon: FileImage },
        ],
      },
      {
        title: "โปรโมชั่น",
        items: [
          { name: "โปรโมชั่น", href: "/promotions", icon: Ticket },
          { name: "รางวัล", href: "/rewards", icon: Gift },
          { name: "รีวิว", href: "/reviews", icon: Star },
        ],
      },
      {
        title: "ตั้งค่า",
        items: [
          { name: "โดเมน", href: "/storefront/domains", icon: Globe },
          { name: "ตั้งค่าร้านค้า", href: "/storefront/settings", icon: Store },
          { name: "ดีไซน์มือถือ", href: "/storefront/mobile-design", icon: Smartphone },
        ],
      },
    ],
  },

  // Mail - Direct Link (placeholder)
  {
    name: "อีเมล",
    href: "/mail",
    icon: Mail,
    color: "bg-gradient-to-br from-blue-500 to-blue-600",
    requiresProvisioning: true,
    appRegistryKey: "mail",
  },

  // Affine - Direct Link (placeholder for embedded Affine)
  {
    name: "โน้ต",
    href: "/affine",
    icon: FileText,
    color: "bg-gradient-to-br from-violet-500 to-violet-600",
    requiresProvisioning: true,
    appRegistryKey: "affine",
  },

  // Blog (Ghost) - Direct Link (opens embedded Ghost Admin)
  {
    name: "บล็อก",
    href: "/blog",
    icon: Newspaper,
    color: "bg-gradient-to-br from-orange-500 to-orange-600",
    requiresProvisioning: true,
    appRegistryKey: "ghost",
  },

  // Sheets (Univer Workspace) - Direct Link (opens embedded Univer at sheets.zaviago.com) — Plan 1966.
  // Shown only to tenants whose CP Tenant.apps has an enabled "univer" row (set by CP
  // control_plane.api.univer.provision_account). The page itself also handles UNIVER_NOT_ENABLED.
  {
    name: "ชีต",
    href: "/sheets",
    icon: FileSpreadsheet,
    color: "bg-gradient-to-br from-emerald-500 to-emerald-600",
    accentColor: "emerald",
    requiresProvisioning: true,
    appRegistryKey: "univer",
    installGatedEverywhere: true,
  },

  // AI Assistant (LobeChat) — TEMPORARILY HIDDEN from the nav (2026-08-27).
  // Route /ai + the SSO/LobeChat stack still work; this only removes the sidebar
  // entry so customers don't see it yet. To re-show: uncomment this block AND the
  // `Bot` icon import near the top of this file.
  // {
  //   name: "AI",
  //   href: "/ai",
  //   icon: Bot,
  //   color: "bg-gradient-to-br from-purple-500 to-purple-600",
  //   accentColor: "purple",
  //   requiresProvisioning: true,
  // },

  // POS — Point of Sale (Plan 125)
  {
    name: "POS",
    href: "/pos",
    icon: Monitor,
    color: "bg-gradient-to-br from-emerald-500 to-emerald-600",
    accentColor: "emerald",
    requiresProvisioning: true,
    appAccessKey: "orders",
  },

  // Fulfillment — Pick & Pack warehouse app with its own sidebar
  {
    name: "การจัดส่ง",
    railLabel: "จัดส่ง",
    href: "/fulfillment",
    icon: PackageCheck,
    color: "bg-gradient-to-br from-indigo-500 to-indigo-600",
    accentColor: "indigo",
    requiresProvisioning: true,
    appAccessKey: "products",
    subMenu: [
      {
        title: "การจัดส่ง",
        items: [
          { name: "หน้าหลัก", href: "/fulfillment", icon: LayoutDashboard },
          { name: "จัดคำสั่งซื้อ", href: "/fulfillment/orders", icon: PackageCheck },
          { name: "งานปรับแต่ง", href: "/fulfillment/personalization", icon: Palette },
          { name: "แพ็คและจัดส่ง", href: "/fulfillment/pack-and-ship", icon: Truck },
          { name: "รอใส่เลขพัสดุ", href: "/fulfillment/awaiting-shipment", icon: PackageOpen },
          { name: "เซสชัน", href: "/fulfillment/sessions", icon: ClipboardCheck },
          { name: "แดชบอร์ด", href: "/fulfillment/dashboard", icon: BarChart3 },
          { name: "ตั้งค่า", href: "/fulfillment/settings", icon: SlidersHorizontal },
        ],
      },
    ],
  },

  // App Store - Direct Link (placeholder for app marketplace)
  {
    name: "แอป",
    href: "/apps",
    icon: LayoutDashboard,
    color: "bg-gradient-to-br from-emerald-500 to-emerald-600",
    requiresProvisioning: true,
  },

  // All Apps - Direct Link (see all apps)
  {
    name: "ทั้งหมด",
    href: "/all-apps",
    icon: Layers,
    color: "bg-gradient-to-br from-gray-500 to-gray-600",
    requiresProvisioning: false,
  },
];
